import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import { RevealSection } from "../motion/RevealSection";
import { api, resolveMediaUrl } from "../../lib/api";
import { scrollToSectionId } from "../../lib/scroll";
import { GalleryLightbox } from "./gallery/GalleryLightbox";

const CATEGORIES = [
  { key: "todas", label: "Todas" },
  { key: "bodas", label: "Bodas" },
  { key: "xv", label: "XV Años" },
  { key: "sesiones", label: "Sesiones de Fotos" },
  { key: "foto_video", label: "Foto + Video" },
  { key: "save_the_date", label: "Save the Date" },
];

const INITIAL_VISIBLE_COUNT = 12;
const GRID_ITEM_INITIAL = { opacity: 0, y: 20 };
const GRID_ITEM_ANIMATE = { opacity: 1, y: 0 };
const GRID_ITEM_EXIT = { opacity: 0 };

const resolveUrl = resolveMediaUrl;

export const Gallery = () => {
  const [gallery, setGallery] = useState([]);
  const [active, setActive] = useState("todas");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get("/gallery").then((r) => setGallery(r.data));
  }, []);

  const filtered = active === "todas" ? gallery : gallery.filter((g) => g.category === active);
  const visible = showAll ? filtered : filtered.slice(0, INITIAL_VISIBLE_COUNT);
  const hasMore = filtered.length > INITIAL_VISIBLE_COUNT;

  const selectCategory = (key) => {
    setActive(key);
    setShowAll(false);
  };

  const openLightbox = (idx) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);
  const nextImage = () => setLightboxIndex((i) => (i + 1) % visible.length);
  const prevImage = () => setLightboxIndex((i) => (i - 1 + visible.length) % visible.length);

  const collapseGallery = () => {
    setShowAll(false);
    scrollToSectionId("galeria");
  };

  return (
    <section id="galeria" className="relative bg-black py-24 md:py-36 px-6 md:px-16">
      <RevealSection>
        <span className="text-twp-gold text-xs uppercase tracking-[0.35em]">03 — Galería</span>
        <h2 className="font-serif-display text-4xl md:text-6xl text-white mt-4 mb-10 max-w-2xl">
          Momentos capturados en el tiempo
        </h2>
      </RevealSection>

      <div className="flex flex-wrap gap-3 mb-12">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            data-testid={`gallery-filter-${c.key}`}
            onClick={() => selectCategory(c.key)}
            className={`px-5 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
              active === c.key ? "bg-twp-gold text-black border-twp-gold" : "border-white/20 text-white/70 hover:border-twp-gold hover:text-twp-gold"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p data-testid="gallery-empty-state" className="text-white/50 text-sm">
          Próximamente compartiremos fotos de esta categoría.
        </p>
      ) : (
        <>
          <div className="columns-2 md:columns-3 lg:columns-4 gap-3 md:gap-4">
            <AnimatePresence initial={false}>
              {visible.map((img, idx) => (
                <motion.div
                  key={img.id}
                  initial={GRID_ITEM_INITIAL}
                  animate={GRID_ITEM_ANIMATE}
                  exit={GRID_ITEM_EXIT}
                  transition={{ duration: 0.4, delay: idx >= INITIAL_VISIBLE_COUNT ? (idx - INITIAL_VISIBLE_COUNT) * 0.03 : idx * 0.03 }}
                  className="mb-3 md:mb-4 break-inside-avoid"
                >
                  <button
                    data-testid={`gallery-image-${idx}`}
                    onClick={() => openLightbox(idx)}
                    className="group relative block w-full overflow-hidden"
                  >
                    <img
                      src={resolveUrl(img.url)}
                      alt={`Galería ${img.category}`}
                      loading="lazy"
                      className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700 brightness-90 group-hover:brightness-100"
                    />
                    <div className="absolute inset-0 border border-white/0 group-hover:border-twp-gold/60 transition-colors duration-300" />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {hasMore && !showAll && (
            <div className="flex justify-center mt-10">
              <button
                data-testid="gallery-show-more-button"
                onClick={() => setShowAll(true)}
                className="flex items-center gap-2 px-8 py-3 border border-twp-gold text-twp-gold text-xs uppercase tracking-[0.25em] font-semibold hover:bg-twp-gold hover:text-black transition-colors duration-300"
              >
                Ver más <ChevronDown size={16} />
              </button>
            </div>
          )}

          {hasMore && showAll && (
            <div className="flex justify-center mt-10">
              <button
                data-testid="gallery-show-less-button"
                onClick={collapseGallery}
                className="flex items-center gap-2 px-8 py-3 border border-white/20 text-white/70 text-xs uppercase tracking-[0.25em] font-semibold hover:border-twp-gold hover:text-twp-gold transition-colors duration-300"
              >
                Ver menos <ChevronUp size={16} />
              </button>
            </div>
          )}
        </>
      )}

      <GalleryLightbox
        imageUrl={lightboxIndex !== null && visible[lightboxIndex] ? resolveUrl(visible[lightboxIndex].url) : null}
        index={lightboxIndex}
        onClose={closeLightbox}
        onNext={nextImage}
        onPrev={prevImage}
      />
    </section>
  );
};
